/**
 * Raptor Predator Frontend - Router Configuration
 * 
 * Application routing with lazy-loaded components and code splitting.
 */

import { lazy, Suspense, useEffect } from 'react';
import { createBrowserRouter, RouterProvider, Outlet, Navigate } from 'react-router-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { useAuthStore } from './stores/authStore';

/**
 * LogoutRedirect - Clears auth state and redirects to login
 * Useful for users who get stuck with stale sessions
 * 
 * Uses synchronous localStorage clear + redirect with query param
 * to ensure the session is cleared before the login page loads.
 */
function LogoutRedirect(): JSX.Element {
  useEffect(() => {
    // SYNCHRONOUSLY clear localStorage before any async operations
    localStorage.removeItem('raptor-auth-store');
    
    // Clear all auth state via store
    useAuthStore.getState().logout();
    
    // Redirect with force=true to ensure login page doesn't redirect back
    window.location.href = '/login?logout=true';
  }, []);
  
  return (
    <div className="flex items-center justify-center min-h-screen bg-slate-900">
      <div className="text-white">Logging out...</div>
    </div>
  );
}

// Layout component (loaded immediately)
// import { AppLayout } from './components/layout/AppLayout'; // Legacy sidebar layout
import { ImmersiveLayout } from './components/layout/ImmersiveLayout'; // New 3D background layout
import { LoadingScreen } from './components/ui/LoadingScreen';
import { ErrorFallback } from './components/ui/ErrorFallback';
import ProtectedRoute from './components/auth/ProtectedRoute';

// Lazy-loaded pages for code splitting
const Dashboard = lazy(() => import('./components/pages/Dashboard'));
const NetworkView = lazy(() => import('./components/pages/NetworkView'));
const ThreatsView = lazy(() => import('./components/pages/ThreatsView'));
const PoliciesView = lazy(() => import('./components/pages/PoliciesView'));
const AdminView = lazy(() => import('./components/pages/AdminView'));
const ExpertView = lazy(() => import('./components/pages/ExpertView'));
const SettingsView = lazy(() => import('./components/pages/SettingsView'));
const WarRoom = lazy(() => import('./components/pages/WarRoomRefactored'));
const BlockchainView = lazy(() => import('./components/pages/BlockchainView'));
const Login = lazy(() => import('./components/pages/Login'));
const Mfa = lazy(() => import('./components/pages/Mfa'));
const TermsAndConditions = lazy(() => import('./components/pages/TermsAndConditions'));
const PrivacyPolicy = lazy(() => import('./components/pages/PrivacyPolicy'));
const OperatorPortal = lazy(() => import('./components/operator/OperatorPortalV3'));

// ============================================================================
// Route Configuration
// ============================================================================

// Get base path from env (set at build time)
const basePath = import.meta.env.BASE_URL || '/';

const router = createBrowserRouter([
  {
    path: '/login',
    element: (
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<LoadingScreen />}>
          <Login />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    path: '/logout',
    element: <LogoutRedirect />,
  },
  {
    path: '/mfa',
    element: (
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<LoadingScreen />}>
          <Mfa />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    path: '/terms',
    element: (
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<LoadingScreen />}>
          <TermsAndConditions />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    path: '/privacy',
    element: (
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <Suspense fallback={<LoadingScreen />}>
          <PrivacyPolicy />
        </Suspense>
      </ErrorBoundary>
    ),
  },
  {
    path: '/',
    element: (
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <ProtectedRoute>
          <ImmersiveLayout>
            <Suspense fallback={<LoadingScreen />}>
              <Outlet />
            </Suspense>
          </ImmersiveLayout>
        </ProtectedRoute>
      </ErrorBoundary>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: 'dashboard',
        element: <Dashboard />,
      },
      {
        path: 'network',
        element: <NetworkView />,
      },
      {
        path: 'threats',
        element: <ThreatsView />,
      },
      {
        path: 'policies',
        element: <PoliciesView />,
      },
      {
        path: 'admin/*',
        element: <AdminView />,
      },
      {
        path: 'expert/*',
        element: <ExpertView />,
      },
      {
        path: 'settings',
        element: <SettingsView />,
      },
      {
        path: 'war-room',
        element: (
          <ProtectedRoute requiredRole="operator">
            <WarRoom />
          </ProtectedRoute>
        ),
      },
      {
        path: 'operator',
        element: (
          <ProtectedRoute requiredRole="operator">
            <OperatorPortal />
          </ProtectedRoute>
        ),
      },
      {
        path: 'blockchain',
        element: <BlockchainView />,
      },
    ],
  },
  {
    path: '*',
    element: <Navigate to="/login" replace />,
  },
], {
  basename: basePath.endsWith('/') ? basePath.slice(0, -1) : basePath,
});

// ============================================================================
// Router Provider Component
// ============================================================================

export function AppRouter(): JSX.Element {
  return <RouterProvider router={router} />;
}

